// Ticket Status Constants
export const TICKET_STATUSES = {
  OPEN: 'open',
  IN_PROGRESS: 'in_progress',
  PENDING_CUSTOMER: 'pending_customer',
  PENDING_INTERNAL: 'pending_internal',
  RESOLVED: 'resolved',
  CLOSED: 'closed',
  ESCALATED: 'escalated'
};

// Ticket Status Labels for UI
export const TICKET_STATUS_LABELS = {
  [TICKET_STATUSES.OPEN]: 'Open',
  [TICKET_STATUSES.IN_PROGRESS]: 'In Progress',
  [TICKET_STATUSES.PENDING_CUSTOMER]: 'Pending Customer',
  [TICKET_STATUSES.PENDING_INTERNAL]: 'Pending Internal',
  [TICKET_STATUSES.RESOLVED]: 'Resolved',
  [TICKET_STATUSES.CLOSED]: 'Closed',
  [TICKET_STATUSES.ESCALATED]: 'Escalated'
};

// Ticket Status Colors for Badges
export const TICKET_STATUS_COLORS = {
  [TICKET_STATUSES.OPEN]: 'info',
  [TICKET_STATUSES.IN_PROGRESS]: 'attention',
  [TICKET_STATUSES.PENDING_CUSTOMER]: 'warning',
  [TICKET_STATUSES.PENDING_INTERNAL]: 'warning',
  [TICKET_STATUSES.RESOLVED]: 'success',
  [TICKET_STATUSES.CLOSED]: 'default',
  [TICKET_STATUSES.ESCALATED]: 'critical'
};

// Ticket Sources
export const TICKET_SOURCES = {
  WEB: 'web',
  EMAIL: 'email',
  PHONE: 'phone',
  WHATSAPP: 'whatsapp',
  INSTAGRAM: 'instagram',
  FACEBOOK: 'facebook',
  TIKTOK: 'tiktok',
  API: 'api',
  ADMIN: 'admin',
  SYSTEM: 'system'
};

// Ticket Source Labels for UI
export const TICKET_SOURCE_LABELS = {
  [TICKET_SOURCES.WEB]: 'Web Portal',
  [TICKET_SOURCES.EMAIL]: 'Email',
  [TICKET_SOURCES.PHONE]: 'Phone',
  [TICKET_SOURCES.WHATSAPP]: 'WhatsApp',
  [TICKET_SOURCES.FACEBOOK]: 'Facebook',
  [TICKET_SOURCES.INSTAGRAM]: 'Instagram',
  [TICKET_SOURCES.TIKTOK]: 'Tiktok',
  [TICKET_SOURCES.API]: 'API',
  [TICKET_SOURCES.ADMIN]: 'Admin Panel',
  [TICKET_SOURCES.SYSTEM]: 'System Generated'
};

// Ticket Types
export const TICKET_TYPES = {
  QUALITY_ISSUE: 'quality_issue',
  DESIGN_CHANGE: 'design_change',
  TECHNICAL_ISSUE: 'technical_issue',
  BILLING_INQUIRY: 'billing_inquiry',
  SHIPPING_ISSUE: 'shipping_issue',
  GENERAL_INQUIRY: 'general_inquiry',
  COMPLAINT: 'complaint',
  FEATURE_REQUEST: 'feature_request'
};

// Ticket Type Labels for UI
export const TICKET_TYPE_LABELS = {
  [TICKET_TYPES.QUALITY_ISSUE]: 'Quality Issue',
  [TICKET_TYPES.DESIGN_CHANGE]: 'Design Change',
  [TICKET_TYPES.TECHNICAL_ISSUE]: 'Technical Issue',
  [TICKET_TYPES.BILLING_INQUIRY]: 'Billing Inquiry',
  [TICKET_TYPES.SHIPPING_ISSUE]: 'Shipping Issue',
  [TICKET_TYPES.GENERAL_INQUIRY]: 'General Inquiry',
  [TICKET_TYPES.COMPLAINT]: 'Complaint',
  [TICKET_TYPES.FEATURE_REQUEST]: 'Feature Request'
};

// Ticket Categories
export const TICKET_CATEGORIES = {
  ORDER_RELATED: 'order_related',
  PRODUCT_RELATED: 'product_related',
  ACCOUNT_RELATED: 'account_related',
  TECHNICAL_SUPPORT: 'technical_support',
  BILLING_SUPPORT: 'billing_support',
  GENERAL_SUPPORT: 'general_support'
};

// Ticket Category Labels for UI
export const TICKET_CATEGORY_LABELS = {
  [TICKET_CATEGORIES.ORDER_RELATED]: 'Order Related',
  [TICKET_CATEGORIES.PRODUCT_RELATED]: 'Product Related',
  [TICKET_CATEGORIES.ACCOUNT_RELATED]: 'Account Related',
  [TICKET_CATEGORIES.TECHNICAL_SUPPORT]: 'Technical Support',
  [TICKET_CATEGORIES.BILLING_SUPPORT]: 'Billing Support',
  [TICKET_CATEGORIES.GENERAL_SUPPORT]: 'General Support'
};

// Priority Levels
export const PRIORITY_LEVELS = {
  LOW: 'low',
  NORMAL: 'normal',
  HIGH: 'high',
  URGENT: 'urgent'
};

// Priority Level Labels for UI
export const PRIORITY_LABELS = {
  [PRIORITY_LEVELS.LOW]: 'Low',
  [PRIORITY_LEVELS.NORMAL]: 'Normal',
  [PRIORITY_LEVELS.HIGH]: 'High',
  [PRIORITY_LEVELS.URGENT]: 'Urgent'
};

// Priority Level Colors for Badges
export const PRIORITY_COLORS = {
  [PRIORITY_LEVELS.LOW]: 'default',
  [PRIORITY_LEVELS.NORMAL]: 'info',
  [PRIORITY_LEVELS.HIGH]: 'warning',
  [PRIORITY_LEVELS.URGENT]: 'critical'
};

// Filter Options for Tickets
export const TICKET_FILTER_OPTIONS = {
  STATUS: Object.entries(TICKET_STATUS_LABELS).map(([value, label]) => ({
    value,
    label
  })),
  TYPE: Object.entries(TICKET_TYPE_LABELS).map(([value, label]) => ({
    value,
    label
  })),
  CATEGORY: Object.entries(TICKET_CATEGORY_LABELS).map(([value, label]) => ({
    value,
    label
  })),
  PRIORITY: Object.entries(PRIORITY_LABELS).map(([value, label]) => ({
    value,
    label
  })),
  SOURCE: Object.entries(TICKET_SOURCE_LABELS).map(([value, label]) => ({
    value,
    label
  }))
};

// Pagination Defaults
export const PAGINATION_DEFAULTS = {
  PAGE: 1,
  PAGE_SIZE: 20,
  SORT_BY: 'created_at',
  SORT_ORDER: 'desc'
};

// Sort Options for Tickets
export const SORT_OPTIONS = [
  { label: 'Newest First', value: 'created_at_desc' },
  { label: 'Oldest First', value: 'created_at_asc' },
  { label: 'Recently Updated', value: 'updated_at_desc' },
  { label: 'Priority High to Low', value: 'priority_desc' },
  { label: 'Priority Low to High', value: 'priority_asc' },
  { label: 'Status A-Z', value: 'status_asc' },
  { label: 'Status Z-A', value: 'status_desc' }
];

// Default form values for creating tickets
export const DEFAULT_TICKET_FORM = {
  title: '',
  description: '',
  type: TICKET_TYPES.GENERAL_INQUIRY,
  category: TICKET_CATEGORIES.GENERAL_SUPPORT,
  priority: PRIORITY_LEVELS.NORMAL,
  customer: {
    name: '',
    email: '',
    phone: ''
  },
  related_order_id: '',
  tags: [],
  source: TICKET_SOURCES.WEB
};

// Validation rules
export const VALIDATION_RULES = {
  TITLE_MIN_LENGTH: 3,
  TITLE_MAX_LENGTH: 200,
  DESCRIPTION_MIN_LENGTH: 10,
  DESCRIPTION_MAX_LENGTH: 5000,
  EMAIL_REGEX: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  PHONE_REGEX: /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,9}$/
};

// Toast messages
export const TOAST_MESSAGES = {
  TICKET_CREATED: 'Ticket created successfully',
  TICKET_UPDATED: 'Ticket updated successfully',
  TICKET_DELETED: 'Ticket deleted successfully',
  STATUS_UPDATED: 'Ticket status updated successfully',
  COMMENT_ADDED: 'Comment added successfully',
  ATTACHMENT_ADDED: 'Attachment added successfully',
  TICKET_ASSIGNED: 'Ticket assigned successfully',
  TICKET_ESCALATED: 'Ticket escalated successfully',
  TAGS_UPDATED: 'Tags updated successfully',
  ERROR_GENERIC: 'An error occurred. Please try again.',
  ERROR_LOAD: 'Failed to load tickets',
  ERROR_CREATE: 'Failed to create ticket',
  ERROR_UPDATE: 'Failed to update ticket',
  ERROR_DELETE: 'Failed to delete ticket'
};

// Table column configurations
export const TABLE_COLUMNS = [
  {
    id: 'ticket_id',
    label: 'Ticket ID',
    sortable: true,
    width: '120px'
  },
  {
    id: 'title',
    label: 'Title',
    sortable: true,
    width: 'auto'
  },
  {
    id: 'type',
    label: 'Type',
    sortable: true,
    width: '150px'
  },
  {
    id: 'category',
    label: 'Category',
    sortable: true,
    width: '150px'
  },
  {
    id: 'status',
    label: 'Status',
    sortable: true,
    width: '140px'
  },
  {
    id: 'priority',
    label: 'Priority',
    sortable: true,
    width: '100px'
  },
  {
    id: 'customer',
    label: 'Customer',
    sortable: false,
    width: '180px'
  },
  {
    id: 'created_at',
    label: 'Created',
    sortable: true,
    width: '140px'
  },
  {
    id: 'actions',
    label: 'Actions',
    sortable: false,
    width: '100px'
  }
];
